import { Api } from 'lib/api.mjs';

/**
 * Settings — ad accounts, Business Managers, the pay structure, banking and
 * the incentive.
 *
 * The pay structure is the one that moves money: changing it changes what the
 * next payout computes to, which is why it is a PUT of the whole config rather
 * than a patch of one field.
 */

/* ── Ad accounts ─────────────────────────────────────────────────────────── */

export function getAccounts({ signal } = {}) {
  return Api.get('/accounts', { signal });
}

/** @param {Object} payload */
export function createAccount(payload) {
  return Api.post('/accounts', { body: payload });
}

/** @param {{ id: string, changes: Object }} payload */
export function updateAccount({ id, changes }) {
  return Api.patch(`/accounts/${id}`, { body: changes });
}

/** @param {{ id: string }} payload */
export function deleteAccount({ id }) {
  return Api.delete(`/accounts/${id}`);
}

/** Copies still running after a connection was removed. */
export function getVaultStatus({ signal } = {}) {
  return Api.get('/accounts/vault-status', { signal });
}

/** Wait until the Archive copy for this connection finishes. */
export async function waitForVault(id) {
  const started = Date.now();
  let missing = 0;
  while (Date.now() - started < 45 * 60 * 1000) {
    await new Promise((r) => setTimeout(r, 2000));
    const s = await getVaultStatus();
    const job = (s?.vaults || []).find((v) => v.id === id);
    if (!job) {
      missing += 1;
      if (missing >= 15) {
        return { ok: false, error: 'The save stopped before it finished. The connection is still there. Try again.' };
      }
      continue;
    }
    missing = 0;
    if (job.running) continue;
    if (job.ok) return { ok: true };
    return { ok: false, error: job.error || 'Facebook could not be read, so nothing was removed.' };
  }
  return { ok: false, error: 'Saving into the Archive is still running. The connection stays until that finishes.' };
}

/**
 * Connect a W account. The key is tested server-side before anything is saved
 * and stored encrypted; the response never carries it back.
 *
 * @param {{ name: string, apiKey: string, accountId: string }} payload
 */
export function createWAccount(payload) {
  return Api.post('/accounts/w', { body: payload });
}

/** Re-test a stored W key. @param {{ id: string }} payload */
export function validateAccount({ id }) {
  return Api.post(`/accounts/${id}/validate`, {});
}

/* ── W daily rows ────────────────────────────────────────────────────────── */

/** The stored per-day W rows and the capture state. @param {{since?, until?}} q */
export function getWDaily({ since, until, signal } = {}) {
  const qs = new URLSearchParams();
  if (since) qs.set('since', since);
  if (until) qs.set('until', until);
  return Api.get(`/w/daily${qs.toString() ? `?${qs}` : ''}`, { signal });
}

/** Capture one past day by hand. @param {{ date?: string }} payload */
export function runWDailySnapshot({ date } = {}) {
  return Api.post('/w/daily/run', { body: date ? { date } : {} });
}

/* ── Business Managers ───────────────────────────────────────────────────── */

export function getBusinessManagers({ signal } = {}) {
  return Api.get('/bms', { signal });
}

/** @param {Object} payload */
export function createBusinessManager(payload) {
  return Api.post('/bms', { body: payload });
}

/** @param {{ id: string, changes: Object }} payload */
export function updateBusinessManager({ id, changes }) {
  return Api.patch(`/bms/${id}`, { body: changes });
}

/** @param {{ id: string, token: string }} payload */
export function updateBusinessManagerToken({ id, token }) {
  return Api.patch(`/bms/${id}/token`, { body: { token } });
}

/** @param {{ id: string }} payload */
export function deleteBusinessManager({ id }) {
  return Api.delete(`/bms/${id}`);
}

/** @param {{ id: string }} payload */
export function validateBusinessManager({ id }) {
  return Api.post(`/bms/${id}/validate`, {});
}

/**
 * The ad accounts a BM can see. A 400 here usually means that BM has no token
 * yet, which the Connections card surfaces on its own.
 *
 * @param {{ id: string }} payload
 */
export function getBusinessManagerAdAccounts({ id, signal }) {
  return Api.get(`/bms/${id}/adaccounts`, { signal });
}

/* ── Pay, banking and incentive ──────────────────────────────────────────── */

export function getPayConfig({ signal } = {}) {
  return Api.get('/pay-config', { signal });
}

/** @param {Object} payload The whole config — see lib/payconfig.mjs. */
export function savePayConfig(payload) {
  return Api.put('/pay-config', { body: payload });
}

export function getCommentThresholds({ signal } = {}) {
  return Api.get('/comment-thresholds', { signal });
}

/** @param {{ spend: number|string, roas: number|string }} payload */
export function saveCommentThresholds(payload) {
  return Api.put('/comment-thresholds', { body: payload });
}

/** @param {Object} payload */
export function saveBanking(payload) {
  return Api.put('/banking', { body: payload });
}

/** @param {Object} payload */
export function saveIncentive(payload) {
  return Api.post('/incentive', { body: payload });
}

/** @param {string} [week] The week's since-date. Without it only the current copy is cleared. */
export function clearIncentive(week) {
  const q = week ? `?week=${encodeURIComponent(week)}` : '';
  return Api.delete(`/incentive${q}`);
}

/** @param {Object} payload */
export function markIncentiveEarned(payload) {
  return Api.post('/incentive/earned', { body: payload });
}
